import { Component, Element, Host, Prop, h } from '@stencil/core';

export interface AhaImageTile extends HTMLAhaImageTileElement {}

/**
 * Renders an image with a label within a button or anchor.
 * @slot overlay - Contents of the overlay slot
 */
@Component({
  tag: 'aha-image-tile',
  styleUrl: 'aha-image-tile.css',
  shadow: true,
})
export class AhaImageTile {
  @Element() host!: HTMLAhaImageTileElement;

  /**
   * URL that specifies the path to the image.
   */
  @Prop() src!: string;

  /**
   * URL that specifies the path to the selected image.
   */
  @Prop({ attribute: 'selectedSrc' }) selectedSrc!: string;

  /**
   * Specifies the URL of the page the link goes to.
   */
  @Prop() href!: string;

  /**
   * Label rendered below the image and used inside the `alt` attribute.
   */
  @Prop() label!: string;

  /**
   * Specifies the ajax method to use
   */
  @Prop() post!: boolean;

  /**
   * Whether the `<aha-image-tile>` is selected or not.
   */
  @Prop({ reflect: true }) selected!: boolean;

  /**
   * Controls if the link should follow its default event or not.
   */
  @Prop() nofollow: boolean = false;

  /**
   * Toggle `aha-image-tile` hover state
   */
  @Prop() hovered!: boolean;

  connectedCallback() {
    this.host.setAttribute('role', 'button');
    this.host.addEventListener('mouseover', this.mouseOverHandler);
    this.host.addEventListener('mouseout', this.mouseOutHandler);
    this.host.addEventListener('focus', this.mouseOverHandler);
    this.host.addEventListener('blur', this.mouseOutHandler);
  }

  disconnectedCallback() {
    this.host.removeEventListener('mouseover', this.mouseOverHandler);
    this.host.removeEventListener('mouseout', this.mouseOutHandler);
    this.host.removeEventListener('focus', this.mouseOverHandler);
    this.host.removeEventListener('blur', this.mouseOutHandler);
  }

  private mouseOverHandler() {
    this.hovered = true;
  }

  private mouseOutHandler() {
    this.hovered = false;
  }

  private cancelLinkClick(event: MouseEvent) {
    if (this.nofollow) {
      event.preventDefault();
    }
  }
  render() {
    const image = this.selected || this.hovered ? this.selectedSrc || this.src : this.src;
    const content = () => (
      <div>
        <div class="overlay-container">
          <div class="image-container">
            <div class="inner-image-container">
              <img src={image} alt={this.label} class="unselected" />
            </div>
          </div>
          <div class="overlay">
            <slot name="overlay"></slot>
          </div>
        </div>
        <p>{this.label}</p>
      </div>
    );

    return (
      <Host>
        {this.href ? (
          <a
            href={`${this.href}`}
            class="tile"
            data-method={`${this.post ? 'post' : undefined}`}
            onClick={e => this.cancelLinkClick(e)}
          >
            {content()}
          </a>
        ) : (
          <div class="tile">
            <button>{content()}</button>
          </div>
        )}
      </Host>
    );
  }
}
